import React, { memo, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeHighlight from 'rehype-highlight';
import 'katex/dist/katex.min.css';
import 'highlight.js/styles/github.css';
import './RichMarkdown.css';

// ─────────────────────────────────────────────────────────
// Helper: split text into protected (code/math) and text segments
// ─────────────────────────────────────────────────────────
function splitProtected(src, re) {
  const parts = [];
  let last = 0, m;
  while ((m = re.exec(src)) !== null) {
    if (m.index > last) parts.push({ type: 'text', s: src.slice(last, m.index) });
    parts.push({ type: 'protected', s: m[0] });
    last = re.lastIndex;
  }
  re.lastIndex = 0;
  if (last < src.length) parts.push({ type: 'text', s: src.slice(last) });
  return parts;
}

function mapTextParts(parts, fn) {
  return parts.map(p => p.type === 'protected' ? p.s : fn(p.s)).join('');
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STAGE 1 — Preprocessing: normalize math delimiters
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// 1a. Convert \[...\] → $$...$$ and \(...\) → $...$
function normalizeLatexDelimiters(src) {
  if (!src) return src;
  const parts = splitProtected(String(src), /(```|~~~)[\s\S]*?\1|`[^`]+`/g);
  return mapTextParts(parts, s => s
    .replace(/\\\[\s*([\s\S]*?)\s*\\\]/g, (_, expr) => `\n$$\n${expr}\n$$\n`)
    .replace(/\\\(\s*([\s\S]*?)\s*\\\)/g, (_, expr) => `$${expr}$`)
  );
}

// 1b. Normalize bare (non-backslash) function names to LaTeX commands.
// E.g. "sin x" → "\sin x", "lim_{h→0}" → "\lim_{h\to 0}"
const BARE_FUNC_NAMES = [
  'sin','cos','tan','cot','sec','csc',
  'arcsin','arccos','arctan','sinh','cosh','tanh',
  'ln','log','exp','lim','limsup','liminf',
  'sup','inf','max','min','det','gcd','deg','dim','ker','hom','Pr',
  'sum','prod','int','iint','iiint','oint',
];
const BARE_FUNC_RE = new RegExp(
  // Only match as standalone word, NOT preceded by backslash, inside math-like context
  // (preceded by space, =, (, [, {, start-of-segment or followed by subscript/arg)
  '(?<!\\\\)\\b(' + BARE_FUNC_NAMES.join('|') + ')(?=\\s*[_^({\\[\\d]|\\s+[a-zA-Z])',
  'g'
);

function addBackslashToBareFuncs(src) {
  if (!src) return src;
  const parts = splitProtected(String(src),
    /(```|~~~)[\s\S]*?\1|`[^`]+`|\$\$[\s\S]*?\$\$|\$[^$\n]+\$/g);
  return mapTextParts(parts, s => {
    BARE_FUNC_RE.lastIndex = 0;
    return s.replace(BARE_FUNC_RE, '\\$1');
  });
}

// Replace common Unicode math arrows/symbols with LaTeX equivalents
function normalizeUnicodeToLatex(src) {
  if (!src) return src;
  const parts = splitProtected(String(src),
    /(```|~~~)[\s\S]*?\1|`[^`]+`/g);
  return mapTextParts(parts, s => s
    // Arrows
    .replace(/→/g, '\\to ')
    .replace(/←/g, '\\leftarrow ')
    .replace(/⇒/g, '\\Rightarrow ')
    .replace(/⇐/g, '\\Leftarrow ')
    .replace(/↔/g, '\\leftrightarrow ')
    .replace(/⇔/g, '\\Leftrightarrow ')
    .replace(/↦/g, '\\mapsto ')
    // Relations
    .replace(/≠/g, '\\neq ')
    .replace(/≤/g, '\\leq ')
    .replace(/≥/g, '\\geq ')
    .replace(/≈/g, '\\approx ')
    .replace(/≡/g, '\\equiv ')
    .replace(/∼/g, '\\sim ')
    .replace(/≅/g, '\\cong ')
    .replace(/∝/g, '\\propto ')
    .replace(/≪/g, '\\ll ')
    .replace(/≫/g, '\\gg ')
    .replace(/⊥/g, '\\perp ')
    // Binary operators
    .replace(/±/g, '\\pm ')
    .replace(/∓/g, '\\mp ')
    .replace(/·/g, '\\cdot ')
    .replace(/×/g, '\\times ')
    .replace(/÷/g, '\\div ')
    .replace(/∘/g, '\\circ ')
    .replace(/⊗/g, '\\otimes ')
    .replace(/⊕/g, '\\oplus ')
    .replace(/∧/g, '\\wedge ')
    .replace(/∨/g, '\\vee ')
    // Set / logic
    .replace(/∈/g, '\\in ')
    .replace(/∉/g, '\\notin ')
    .replace(/⊂/g, '\\subset ')
    .replace(/⊃/g, '\\supset ')
    .replace(/⊆/g, '\\subseteq ')
    .replace(/⊇/g, '\\supseteq ')
    .replace(/∪/g, '\\cup ')
    .replace(/∩/g, '\\cap ')
    .replace(/∀/g, '\\forall ')
    .replace(/∃/g, '\\exists ')
    .replace(/∅/g, '\\emptyset ')
    .replace(/¬/g, '\\neg ')
    // Calculus / diff operators
    .replace(/∂/g, '\\partial ')
    .replace(/∇/g, '\\nabla ')
    .replace(/∞/g, '\\infty ')
    // Big operators (standalone Unicode symbols)
    .replace(/∑/g, '\\sum ')
    .replace(/∏/g, '\\prod ')
    .replace(/∫/g, '\\int ')
    .replace(/∬/g, '\\iint ')
    .replace(/∭/g, '\\iiint ')
    .replace(/∮/g, '\\oint ')
    // Greek lowercase
    .replace(/α/g, '\\alpha ')
    .replace(/β/g, '\\beta ')
    .replace(/γ/g, '\\gamma ')
    .replace(/δ/g, '\\delta ')
    .replace(/ε/g, '\\varepsilon ')
    .replace(/ζ/g, '\\zeta ')
    .replace(/η/g, '\\eta ')
    .replace(/θ/g, '\\theta ')
    .replace(/ι/g, '\\iota ')
    .replace(/κ/g, '\\kappa ')
    .replace(/λ/g, '\\lambda ')
    .replace(/μ/g, '\\mu ')
    .replace(/ν/g, '\\nu ')
    .replace(/ξ/g, '\\xi ')
    .replace(/π/g, '\\pi ')
    .replace(/ρ/g, '\\rho ')
    .replace(/σ/g, '\\sigma ')
    .replace(/ς/g, '\\varsigma ')
    .replace(/τ/g, '\\tau ')
    .replace(/υ/g, '\\upsilon ')
    .replace(/φ/g, '\\varphi ')
    .replace(/χ/g, '\\chi ')
    .replace(/ψ/g, '\\psi ')
    .replace(/ω/g, '\\omega ')
    // Greek uppercase
    .replace(/Γ/g, '\\Gamma ')
    .replace(/Δ/g, '\\Delta ')
    .replace(/Θ/g, '\\Theta ')
    .replace(/Λ/g, '\\Lambda ')
    .replace(/Ξ/g, '\\Xi ')
    .replace(/Π/g, '\\Pi ')
    .replace(/Σ/g, '\\Sigma ')
    .replace(/Φ/g, '\\Phi ')
    .replace(/Ψ/g, '\\Psi ')
    .replace(/Ω/g, '\\Omega ')
    // Superscript digits → ^{n}
    .replace(/⁰/g, '^{0}')
    .replace(/¹/g, '^{1}')
    .replace(/²/g, '^{2}')
    .replace(/³/g, '^{3}')
    .replace(/⁴/g, '^{4}')
    .replace(/⁵/g, '^{5}')
    .replace(/⁶/g, '^{6}')
    .replace(/⁷/g, '^{7}')
    .replace(/⁸/g, '^{8}')
    .replace(/⁹/g, '^{9}')
    .replace(/⁺/g, '^{+}')
    .replace(/⁻/g, '^{-}')
    .replace(/ⁿ/g, '^{n}')
    // Subscript digits → _{n}
    .replace(/₀/g, '_{0}')
    .replace(/₁/g, '_{1}')
    .replace(/₂/g, '_{2}')
    .replace(/₃/g, '_{3}')
    .replace(/₄/g, '_{4}')
    .replace(/₅/g, '_{5}')
    .replace(/₆/g, '_{6}')
    .replace(/₇/g, '_{7}')
    .replace(/₈/g, '_{8}')
    .replace(/₉/g, '_{9}')
    // Misc symbols
    .replace(/ℏ/g, '\\hbar ')
    .replace(/ℓ/g, '\\ell ')
    .replace(/℘/g, '\\wp ')
    .replace(/ℜ/g, '\\Re ')
    .replace(/ℑ/g, '\\Im ')
    .replace(/†/g, '\\dagger ')
    .replace(/‡/g, '\\ddagger ')
    .replace(/⟨/g, '\\langle ')
    .replace(/⟩/g, '\\rangle ')
    // Accented letters used as math notation (time derivatives, etc.)
    .replace(/ȧ/g, '\\dot{a}')
    .replace(/ḃ/g, '\\dot{b}')
    .replace(/ċ/g, '\\dot{c}')
    .replace(/ḋ/g, '\\dot{d}')
    .replace(/ė/g, '\\dot{e}')
    .replace(/ḟ/g, '\\dot{f}')
    .replace(/ġ/g, '\\dot{g}')
    .replace(/ḣ/g, '\\dot{h}')
    .replace(/ṁ/g, '\\dot{m}')
    .replace(/ṅ/g, '\\dot{n}')
    .replace(/ȯ/g, '\\dot{o}')
    .replace(/ṗ/g, '\\dot{p}')
    .replace(/ṙ/g, '\\dot{r}')
    .replace(/ṡ/g, '\\dot{s}')
    .replace(/ṫ/g, '\\dot{t}')
    .replace(/ẇ/g, '\\dot{w}')
    .replace(/ẋ/g, '\\dot{x}')
    .replace(/ẏ/g, '\\dot{y}')
    .replace(/ż/g, '\\dot{z}')
  );
}

// 1b-1.5. Split heading markers that appear mid-line onto their own lines.
// After fixMathSpacing puts $$ on own lines, text between $$ blocks may
// contain multiple heading markers jammed together, e.g.
//   "#### (RG flow). ### 3) Title" → "#### (RG flow).\n\n### 3) Title"
function splitInlineHeadings(src) {
  if (!src) return src;
  const parts = splitProtected(String(src),
    /(```|~~~)[\s\S]*?\1|`[^`]+`|\$\$[\s\S]*?\$\$|\$[^$\n]+\$/g);
  return mapTextParts(parts, s =>
    s.replace(/([^\n])[ \t]*(#{1,6}\s+)/g, (match, pre, heading) =>
      pre + '\n\n' + heading
    )
  );
}

// 1b-2. Split heading lines that contain embedded equations.
// E.g. "### 4) Euler equations (inviscid fluid) \partial u/\partial t = 0"
// → "### 4) Euler equations (inviscid fluid)\n\n$$\n\partial u/\partial t = 0\n$$"
const MATH_CMD_RE = /\\(?:partial|nabla|frac|dfrac|tfrac|sqrt|int|iint|iiint|oint|sum|prod|lim|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|varsigma|tau|upsilon|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|infty|cdot|times|div|pm|mp|leq|geq|neq|approx|equiv|sim|to|rightarrow|leftarrow|Rightarrow|Leftarrow|vec|hat|bar|tilde|overline|underline|binom|operatorname|hbar|ell|forall|exists|emptyset|subset|supset|cup|cap|in|notin|wedge|vee|otimes|oplus|perp|propto)(?![a-zA-Z])/;

function splitHeadingMath(src) {
  if (!src) return src;
  const parts = splitProtected(String(src),
    /(```|~~~)[\s\S]*?\1|`[^`]+`|\$\$[\s\S]*?\$\$|\$[^$\n]+\$/g);
  return mapTextParts(parts, s =>
    s.split('\n').map(line => {
      const headingMatch = line.match(/^(\s*#{1,6}\s+)(.*)/);
      if (!headingMatch) return line;
      const hPrefix = headingMatch[1];
      const content = headingMatch[2];
      const cmdMatch = content.match(MATH_CMD_RE);
      if (!cmdMatch) return line;
      const splitIdx = cmdMatch.index;
      const descPart = content.slice(0, splitIdx).trim();
      const mathPart = content.slice(splitIdx).trim();
      // Only split if math portion is substantial
      const cmdCount = (mathPart.match(/\\[a-zA-Z]+/g) || []).length;
      if (cmdCount < 2 && !/[=<>]/.test(mathPart)) return line;
      if (!descPart) return `${hPrefix}\n\n$$\n${mathPart}\n$$`;
      return `${hPrefix}${descPart}\n\n$$\n${mathPart}\n$$`;
    }).join('\n')
  );
}

// 1b-3. Detect segments of text that look like math expressions and
// wrap them in $...$ (inline) or $$...$$ (display). This handles the case
// where the LLM returns an equation entirely in plain text without
// any LaTeX delimiters. Runs AFTER normalizeUnicodeToLatex has already
// converted individual Unicode symbols to \commands.
const MATH_INDICATOR_RE = /\\(?:frac|sqrt|partial|nabla|infty|alpha|beta|gamma|delta|varepsilon|zeta|eta|theta|iota|kappa|lambda|mu|nu|xi|pi|rho|sigma|tau|upsilon|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|int|iint|iiint|oint|sum|prod|lim|sin|cos|tan|log|ln|exp|to|leftarrow|Rightarrow|Leftarrow|leq|geq|neq|approx|equiv|sim|pm|mp|cdot|times|div|forall|exists|emptyset|subset|supset|cup|cap|in|notin|hbar|ell|wedge|vee|otimes|oplus|perp|propto)(?![a-zA-Z])/g;

function wrapUnicodeMathExpressions(src) {
  if (!src) return src;
  const text = String(src);

  // Work line-by-line, protecting code blocks and already-delimited math
  const segs = splitProtected(text,
    /(```|~~~)[\s\S]*?\1|`[^`]+`|\$\$[\s\S]*?\$\$|\$[^$\n]+\$/g);

  return segs.map(seg => {
    if (seg.type === 'protected') return seg.s;

    // Process each line independently
    return seg.s.split('\n').map(line => {
      // Skip heading lines (splitHeadingMath already processed these)
      // and blockquote-only lines
      if (/^\s*(#{1,6}\s|>\s*$)/.test(line)) return line;

      // Count math-indicator tokens in this line
      MATH_INDICATOR_RE.lastIndex = 0;
      const mathIndicators = (line.match(MATH_INDICATOR_RE) || []).length;
      // Also count ^{} and _{} patterns as extra math signal
      const subSupCount = (line.match(/[_^]\s*\{[^}]*\}/g) || []).length;
      const totalMath = mathIndicators + subSupCount;

      // Need at least 2 math tokens and equation-like structure
      const hasEquationStructure = /[=<>]/.test(line) || totalMath >= 3;

      if (totalMath >= 2 && hasEquationStructure) {
        const trimmed = line.trim();

        // Handle common connector words ("with", "where", etc.)
        // E.g. "with \tau = \mu ..." → "with $\tau = \mu ...$"
        const connectorRe = /^([-*+]\s+|\d+\.\s+)?(with|where|for|given|such that|when|if|and|or|here|noting|setting)\s+(.+)/i;
        const connectorMatch = trimmed.match(connectorRe);
        if (connectorMatch) {
          const bullet = connectorMatch[1] || '';
          const word = connectorMatch[2];
          const rest = connectorMatch[3];
          const restMath = (rest.match(/\\[a-zA-Z]+/g) || []).length;
          if (restMath >= 2) {
            return `${bullet}${word} $${rest.trim()}$`;
          }
        }

        // Determine if this is a display math candidate
        const leadingProseWords = (trimmed.match(/^[A-Za-z][A-Za-z,;:'"\s]*/)?.[0] || '').trim().split(/\s+/).filter(Boolean);
        const isDisplayCandidate = leadingProseWords.length <= 2 && totalMath >= 2;

        if (isDisplayCandidate && trimmed.length > 0) {
          // List items — wrap just the math portion after the bullet
          const bulletMatch = trimmed.match(/^([-*+]\s+|\d+\.\s+)(.*)/);
          if (bulletMatch) {
            const prefix = bulletMatch[1];
            const rest = bulletMatch[2];
            const restMath = (rest.match(/\\[a-zA-Z]+/g) || []).length;
            if (restMath >= 2) {
              return `${prefix}$${rest}$`;
            }
            return line;
          }
          // Wrap as display math on own lines
          return `\n$$\n${trimmed}\n$$\n`;
        }

        // Inline: find the math-heavy substring and wrap it
        const mathRun = line.match(/(\\[a-zA-Z]+[^]*)/)?.[0];
        if (mathRun && mathRun.length > 4) {
          return line.replace(mathRun, `$${mathRun.trim()}$`);
        }
      }

      return line;
    }).join('\n');
  }).join('');
}

// 1b. Wrap bare LaTeX the model outputs without delimiters
function wrapBareLatex(src) {
  if (!src) return src;
  const text = String(src);

  const barePatterns = [
    // LaTeX commands with brace arguments: \frac{a}{b}, \sqrt{x}
    /\\(?:frac|dfrac|tfrac|sqrt|vec|hat|bar|dot|ddot|tilde|overline|underline|overbrace|underbrace|binom|text|mathrm|mathbf|mathit|mathbb|mathcal|operatorname)\s*\{[^}]*\}(?:\s*\{[^}]*\})?(?:[_^]\s*(?:\{[^}]*\}|[a-zA-Z0-9]))*[^$\n]*/g,

    // Standalone LaTeX commands ± subscripts: \int_0^1, \sum_{n=1}^{N}, \alpha
    /\\(?:int|iint|iiint|oint|sum|prod|lim|limsup|liminf|sup|inf|max|min|det|gcd|ln|log|sin|cos|tan|cot|sec|csc|arcsin|arccos|arctan|sinh|cosh|tanh|exp|deg|dim|ker|hom|Pr|partial|nabla|infty|forall|exists|emptyset|alpha|beta|gamma|delta|epsilon|varepsilon|zeta|eta|theta|lambda|mu|nu|xi|pi|rho|sigma|tau|phi|varphi|chi|psi|omega|Gamma|Delta|Theta|Lambda|Xi|Pi|Sigma|Phi|Psi|Omega|cdot|times|div|pm|mp|leq|geq|neq|approx|equiv|sim|cong|propto|perp|in|notin|subset|supset|subseteq|supseteq|cup|cap|setminus|to|rightarrow|leftarrow|Rightarrow|Leftarrow|mapsto)(?:[_^]\s*(?:\{[^}]*\}|[a-zA-Z0-9]))*(?:\s*[a-zA-Z0-9+\-*/=()[\],. ]*[_^]\s*(?:\{[^}]*\}|[a-zA-Z0-9]))*/g,

    // Identifier + braced sub/superscript: x_{n+1}, f^{(k)}, lim_{h→0}
    /[a-zA-Z][a-zA-Z0-9']*[_^]\s*\{[^}]*\}(?:\s*[_^]\s*(?:\{[^}]*\}|[a-zA-Z0-9]))*/g,

    // Parenthesized expression with superscript: (x+h)^2, (1+x^2)^{n}
    /\([^)]{1,60}\)\s*\^\s*(?:\{[^}]*\}|[a-zA-Z0-9]+)/g,

    // Function name + braced subscript: lim_{h→0}, log_{10}
    /(?:lim|log|sup|inf|max|min|det|gcd)[_^]\s*\{[^}]*\}/g,

    // Simple superscript without braces: x^2, e^x
    /(?<![a-zA-Z_$\\])([a-zA-Z][a-zA-Z0-9]*)\^([0-9]+|[a-zA-Z])(?![a-zA-Z{])/g,
  ];

  // Apply each pattern, re-splitting after each to skip already-wrapped math
  let result = text;
  for (const re of barePatterns) {
    const segs = splitProtected(result,
      /(```|~~~)[\s\S]*?\1|`[^`]+`|\$\$[\s\S]*?\$\$|\$[^$\n]+\$/g);
    result = segs.map(seg => {
      if (seg.type === 'protected') return seg.s;
      re.lastIndex = 0;
      return seg.s.replace(re, match => {
        const t = match.trim();
        return t ? '$' + t + '$' : match;
      });
    }).join('');
  }
  return result;
}

// 1c. Ensure $$ delimiters sit on their own lines.
// Uses function replacements to avoid JS replacement-string $$ escaping issues.
function fixMathSpacing(src) {
  if (!src) return src;
  const parts = splitProtected(String(src), /(```|~~~)[\s\S]*?\1/g);
  return mapTextParts(parts, s => {
    // Add newline before $$ if preceded by non-newline content (+ optional spaces/tabs)
    let r = s.replace(/([^\n])[ \t]*\$\$/g, (_, pre) => pre + '\n$$');
    // Add newline after $$ if followed by non-newline content (+ optional spaces/tabs)
    r = r.replace(/\$\$[ \t]*([^\n])/g, (_, post) => '$$\n' + post);
    return r;
  });
}

// 1c-2. Split display math blocks that contain blank lines into separate blocks.
// remark-math closes display math at blank lines, so a blank line inside $$...$$
// causes the block to end early and leaves orphaned $$ that swallow subsequent
// headings into math mode (rendering everything red).
function splitDisplayMathOnBlankLines(src) {
  if (!src) return src;
  // Match display math blocks: $$ on own line, content, $$ on own line
  return String(src).replace(/\$\$\n([\s\S]*?)\n\$\$/g, (match, inner) => {
    // If no blank lines inside, return as-is
    if (!/\n[ \t]*\n/.test(inner)) return match;

    // If it contains \begin{...}, don't split — just collapse blank lines
    // to preserve aligned/cases/matrix environments
    if (/\\begin\{/.test(inner)) {
      return `$$\n${inner.replace(/\n[ \t]*\n/g, '\n')}\n$$`;
    }

    // Split into separate $$ blocks at each blank line
    const parts = inner.split(/\n[ \t]*\n/).map(p => p.trim()).filter(Boolean);
    if (parts.length <= 1) return match;
    return parts.map(p => `$$\n${p}\n$$`).join('\n\n');
  });
}

// 1c-3. Fix display math blocks: auto-close orphan $$ openers at blank
// lines or headings, and ensure blank lines surround $$ blocks so that
// remark-math recognises them as display math (requires a new paragraph).
function fixDisplayMathBlocks(src) {
  if (!src) return src;
  const lines = String(src).split('\n');
  let isInsideMath = false;
  let codeFence = null;
  const result = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Track fenced code blocks — skip everything inside them
    if (codeFence === null && /^(```|~~~)/.test(trimmed)) {
      codeFence = trimmed.match(/^(```|~~~)/)[1];
      result.push(line);
      continue;
    }
    if (codeFence && trimmed.startsWith(codeFence)) {
      codeFence = null;
      result.push(line);
      continue;
    }
    if (codeFence) {
      result.push(line);
      continue;
    }

    if (trimmed === '$$') {
      if (!isInsideMath) {
        // Opening $$: ensure blank line before so remark-math sees a new paragraph
        if (result.length > 0 && result[result.length - 1].trim() !== '') {
          result.push('');
        }
        result.push(line);
        isInsideMath = true;
      } else {
        // Closing $$
        result.push(line);
        isInsideMath = false;
        // Ensure blank line after closer
        if (i + 1 < lines.length && lines[i + 1]?.trim() !== '') {
          result.push('');
        }
      }
    } else if (isInsideMath) {
      // Inside an open $$ block
      if (trimmed === '' || /^#{1,6}\s/.test(trimmed)) {
        // Blank line or heading inside math → auto-close the block
        result.push('$$');
        result.push('');
        isInsideMath = false;
        result.push(line);
      } else {
        result.push(line);
      }
    } else {
      result.push(line);
    }
  }

  // Close if still open at end of input
  if (isInsideMath) {
    result.push('$$');
  }

  return result.join('\n');
}

// 1d. Extract trailing text descriptions from display math blocks.
// The model often puts equation names inside $$...$$ like:
//   $$i\partial_t \psi + \nabla^2 \psi = 0 \quad \text{(NLS)}$$
//   $$\partial_t u + 6u\partial_x u = 0 \quad (KdV equation)$$
// KaTeX renders the bare text as math-mode characters in a narrow column.
// This step moves such descriptions outside the math block.
function extractTextFromDisplayMath(src) {
  if (!src) return src;
  return String(src).replace(/\$\$([\s\S]*?)\$\$/g, (match, inner) => {
    // Pattern 1: trailing \text{...} or \textbf{...} at end of display math
    // e.g. $$...= 0 \quad \text{(cubic NLS)}$$
    const texCmdRe = /^([\s\S]*?\S)\s*(?:\\quad|\\qquad|\\,|\\;|\\!)?\s*\\text(?:bf|it|rm|sf)?\{([^}]+)\}\s*\.?\s*$/;
    const texMatch = inner.match(texCmdRe);
    if (texMatch) {
      const mathPart = texMatch[1].trim();
      const textDesc = texMatch[2].trim();
      return `$$\n${mathPart}\n$$\n${textDesc}`;
    }

    // Pattern 2: trailing bare parenthetical with mostly-alpha content (equation name)
    // e.g. $$...= 0 (cubic nonlinear Schrödinger equation)$$
    // Must contain at least 2 consecutive alpha chars to distinguish from math like (x+1)
    const bareParenRe = /^([\s\S]*?\S)\s*(?:\\quad|\\qquad|\\,|\\;|\\!)?\s*\(([A-Za-zÀ-ÿ][\w\s\-–—,;:'''"""']*[A-Za-zÀ-ÿ).])\)\s*\.?\s*$/;
    const bareMatch = inner.match(bareParenRe);
    if (bareMatch) {
      const desc = bareMatch[2].trim();
      // Only extract if description is mostly text (not math like (x+1))
      const alphaRatio = (desc.match(/[A-Za-zÀ-ÿ\s]/g) || []).length / desc.length;
      if (alphaRatio > 0.6 && desc.length > 3) {
        const mathPart = bareMatch[1].trim();
        return `$$\n${mathPart}\n$$\n(${desc})`;
      }
    }

    // Pattern 3: trailing bare text without parentheses after the last math symbol
    // e.g. $$...= 0, \quad Euler–Lagrange equation.$$
    const trailingTextRe = /^([\s\S]*?(?:[=><\d})\]|]|\$))\s*(?:\\quad|\\qquad|,)?\s+([A-Z][A-Za-zÀ-ÿ\s\-–—'']+(?:equation|formula|identity|theorem|law|rule|lemma|principle|inequality|relation|transform)[s.]?\s*\.?\s*)$/;
    const trailMatch = inner.match(trailingTextRe);
    if (trailMatch) {
      const mathPart = trailMatch[1].trim();
      const textDesc = trailMatch[2].trim();
      return `$$\n${mathPart}\n$$\n${textDesc}`;
    }

    return match;
  });
}

// Combined Stage 1 entry point
function preprocessMath(src) {
  if (!src) return '';
  let s = normalizeLatexDelimiters(src);
  s = fixMathSpacing(s);                 // early: normalize so $$ sit on own lines
  s = splitInlineHeadings(s);            // split "#### (desc). ### 3)" mid-line headings
  s = fixDisplayMathBlocks(s);           // close orphan $$ + blank-line-surround blocks
  s = splitDisplayMathOnBlankLines(s);   // split $$ blocks with blank lines into separate blocks
  s = splitHeadingMath(s);               // split "### Desc \equation" into heading + $$math$$
  s = addBackslashToBareFuncs(s);
  s = normalizeUnicodeToLatex(s);
  s = wrapUnicodeMathExpressions(s);     // detect & wrap full math lines
  s = wrapBareLatex(s);
  s = fixMathSpacing(s);                 // again: catch any new adjacent $$ from wrapping
  s = fixDisplayMathBlocks(s);           // again: close orphans + paragraph breaks
  s = splitDisplayMathOnBlankLines(s);   // again: catch any newly-created blocks with blanks
  s = extractTextFromDisplayMath(s);
  return s;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STAGE 2 — Streaming safety: hide incomplete math
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

function trimIncompleteMath(src) {
  if (!src) return src;
  let s = String(src);

  // Strip trailing incomplete \( or \[  (no closing pair yet)
  s = s.replace(/\\[([]*$/, '');

  // Handle incomplete $$ block:
  // Find last $$ — if it's an opener without a closer, trim from it
  const lastDoubleDollar = s.lastIndexOf('$$');
  if (lastDoubleDollar !== -1) {
    // Count $$ occurrences to see if they're paired
    const before = s.slice(0, lastDoubleDollar);
    const ddCount = (before.match(/\$\$/g) || []).length;
    // Odd count of previous $$ means the last one is an opener — trim it
    if (ddCount % 2 === 0) {
      // The last $$ is a new opener without closer — remove from there
      s = s.slice(0, lastDoubleDollar);
    }
  }

  // Handle incomplete single $ (inline):
  // Find last $ that isn't part of $$
  // Replace $$ temporarily, scan for unpaired single $
  const withoutDD = s.replace(/\$\$/g, '\x00\x00');
  const lastSingle = withoutDD.lastIndexOf('$');
  if (lastSingle !== -1) {
    // Count single $ before this position
    const beforeSingle = withoutDD.slice(0, lastSingle);
    const singleCount = (beforeSingle.match(/\$/g) || []).length;
    if (singleCount % 2 === 0) {
      // This last $ is an opener — trim from it
      s = s.slice(0, lastSingle);
    }
  }

  return s;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STAGE 3 support — Compact / Enhance markdown (unchanged)
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// Compact markdown outside of fenced code blocks (``` or ~~~)
// - Normalizes CRLF
// - Removes whitespace-only lines
// - Collapses 3+ blank lines (with or without spaces) to exactly 1 blank line
// - Tightens blank lines before lists, headings, and blockquotes
function compactMarkdownOutsideCode(src){
  if (!src) return '';
  const text = String(src).replace(/\r\n?/g, '\n');

  // Split into [outside, code, outside, code, ...]
  // Protect fenced code blocks AND display math blocks from compaction
  const fenceRe = /(```|~~~)[\s\S]*?\1|\$\$[\s\S]*?\$\$/g;

  const parts = [];
  let last = 0, m;
  while ((m = fenceRe.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', s: text.slice(last, m.index) });
    parts.push({ type: 'code', s: m[0] });
    last = fenceRe.lastIndex;
  }
  if (last < text.length) parts.push({ type: 'text', s: text.slice(last) });

  const out = parts.map((p, idx) => {
    if (p.type === 'code') return p.s;

    let s = p.s;

    // Trim trailing spaces at EOL
    s = s.replace(/[ \t]+(\n)/g, '$1');

    // Remove lines that contain only whitespace
    s = s.replace(/^[ \t]+$/gm, '');

    // Collapse runs of blank/whitespace-only lines to a single blank line
    // Handles sequences like "\n \n \n" or "\n\n\n\n"
    s = s.replace(/(?:[ \t]*\n){3,}/g, '\n\n');

    // Tighten lists: remove blank lines before list bullets/ordered items
    s = s.replace(/\n{2,}([ \t]*([-*+]|(\d+\.))\s)/g, '\n$1');

    // Tighten before headings and blockquotes
    s = s.replace(/\n{2,}([ \t]*#{1,6}\s)/g, '\n$1');
    s = s.replace(/\n{2,}([ \t]*>\s)/g, '\n$1');

    // Trim leading/trailing blank lines, BUT preserve blank lines at
    // boundaries with display math blocks — remark-math requires $$
    // to start a new paragraph (preceded/followed by blank line).
    const prevIsMath = idx > 0 && parts[idx - 1].type === 'code' && parts[idx - 1].s.startsWith('$$');
    const nextIsMath = idx < parts.length - 1 && parts[idx + 1].type === 'code' && parts[idx + 1].s.startsWith('$$');
    if (prevIsMath) {
      s = s.replace(/^\n{3,}/, '\n\n');  // collapse excess, keep 1 blank line
    } else {
      s = s.replace(/^\n+/, '');
    }
    if (nextIsMath) {
      s = s.replace(/\n{3,}$/, '\n\n');  // collapse excess, keep 1 blank line
    } else {
      s = s.replace(/\n+$/, '');
    }

    return s;
  });

  return out.join('');
}

// Promote headings/bold outside fenced code blocks.
// - Standalone short lines between blank lines => "### Heading"
// - In list items, "Label: value" => "**Label:** value"
function enhanceMarkdownOutsideCode(src){
  if (!src) return '';
  const text = String(src);

  // Protect fenced code blocks AND display math blocks from enhancement
  const fenceRe = /(```|~~~)[\s\S]*?\1|\$\$[\s\S]*?\$\$/g;
  const parts = [];
  let last = 0, m;
  while ((m = fenceRe.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: 'text', s: text.slice(last, m.index) });
    parts.push({ type: 'code', s: m[0] });
    last = fenceRe.lastIndex;
  }
  if (last < text.length) parts.push({ type: 'text', s: text.slice(last) });

  const out = parts.map(p => {
    if (p.type === 'code') return p.s;

    const lines = p.s.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const prevBlank = i === 0 || lines[i-1].trim() === '';
      const nextBlank = i === lines.length - 1 || lines[i+1].trim() === '';
      const raw = lines[i];
      const t = raw.trim();

      // Promote standalone titles to headings
      if (
        t &&
        prevBlank &&
        nextBlank &&
        t.length <= 80 &&
        // not already markdown/HTML block or math
        !/^#{1,6}\s/.test(t) &&
        !/^([-*+])\s/.test(t) &&
        !/^\d+\.\s/.test(t) &&
        !/^>/.test(t) &&
        !/^<\w+/.test(t) &&
        !/\$/.test(t)
      ) {
        const title = t.replace(/\s*:\s*$/, ''); // drop trailing colon if present
        lines[i] = `### ${title}`;
        continue;
      }

      // Bold "Label: value" at start of list items
      // e.g. "- Risks and countermeasures: details" => "- **Risks and countermeasures:** details"
      lines[i] = lines[i].replace(
        /^([ \t]*([-*+]|(\d+\.))\s+)([^:\n]{2,60}?):(\s+)/,
        (_m, lead, _bullet, _num, label, space) => `${lead}**${label}:**${space}`
      );
    }
    return lines.join('\n');
  });

  return out.join('');
}

function LinkRenderer({ href = '', children }) {
  const ext = /^https?:\/\//i.test(href);
  return (
    <a href={href} target={ext ? '_blank' : undefined} rel={ext ? 'noopener noreferrer nofollow' : undefined}>
      {children}
    </a>
  );
}

function Checkbox(props) {
  return <input type="checkbox" checked={!!props.checked} readOnly disabled />;
}

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// STAGE 3 — Render: ReactMarkdown + remark-math + rehype-katex
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

const RichMarkdown = memo(function RichMarkdown({ text = '', compact = true, isStreaming = false }) {
  const cleaned = useMemo(() => {
    if (!text) return '';
    // Stage 1: preprocess math delimiters
    let s = preprocessMath(text);
    // Stage 2: during streaming, trim incomplete math so KaTeX doesn't choke
    if (isStreaming) {
      s = trimIncompleteMath(s);
    }
    // Compact whitespace (outside code + math blocks)
    s = compactMarkdownOutsideCode(s);
    // Safety net: restore blank-line surrounds for $$ after compaction
    s = fixDisplayMathBlocks(s);
    if (!compact) return s;
    // Enhance structure (headings, bold labels)
    s = enhanceMarkdownOutsideCode(s);
    // Final safety: ensure $$ blocks still have blank-line surrounds
    s = fixDisplayMathBlocks(s);

    // ── Diagnostic logging (only on FINAL render, not during streaming) ──
    if (!isStreaming && (text.includes('$$') || text.includes('\\') || /[∂∇∑∫αβγδρμ²³]/.test(text))) {
      console.warn('[RichMarkdown MATH_DEBUG]', {
        rawLen: text.length,
        rawFirst300: text.slice(0, 300),
        cleanedFirst300: s.slice(0, 300),
        ddCount: (s.match(/\$\$/g) || []).length,
      });
    }

    return s;
  }, [text, compact, isStreaming]);

  return (
    <div className="md-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          [rehypeKatex, { throwOnError: false }],
          [rehypeHighlight, { ignoreMissing: true }],
        ]}
        components={{
          a: LinkRenderer,
          input: Checkbox,
          table: (p) => <table className="md-table" {...p} />,
          blockquote: (p) => <blockquote className="md-quote" {...p} />,
          hr: (p) => <hr className="md-hr" {...p} />,
          img: (p) => <img loading="lazy" {...p} alt={p.alt ?? ''} />
        }}
      >
        {cleaned}
      </ReactMarkdown>
    </div>
  );
});

export default RichMarkdown;